/*****************************************************************
 *  CUSTOMER BUTLER - ZOHO'S LIST PIECES, IN THE HR LOOK           *
 *  File: apps/web/src/pages/customer/ZohoListParts.js             *
 *  What it does: the pieces of Zoho's Contacts list the office    *
 *  uses, added to HR's roster (which stays the default):          *
 *    FilterRail       the left "Filter Contacts by" rail: saved   *
 *                     views + "Filter By Fields" (tick a field,   *
 *                     type what it contains); folds away; on a    *
 *                     phone it opens as a sheet                   *
 *    ViewTypeSwitch   Table (HR's table) or Cards (Zoho's card    *
 *                     rows) - a second view ADDED, not replacing  *
 *    PerPagePicker    "Records per page"                          *
 *    ZohoCardRow      one Zoho card row: letter tile, name, the   *
 *                     Phone / Email / Account lines, owner and    *
 *                     last-modified on the right, next-task tag   *
 *  Reads: what the list hands it.  Writes: per-browser choices    *
 *  (view type, rail folded) only.                                 *
 *****************************************************************/

/* WHY (2026-10-15, James via Fable, Zoho tour frame 080 - the Contacts
   list as the team sees it): keep HR's chrome and table (approved), and
   add Zoho's pieces that matter to the office - the filter rail, the
   view-type toggle, "Records per page", "Total Records", and the owner +
   last-modified on each row. Primary button stays "+ Add customer". */

/*****************************************************************
 *  TABLE OF CONTENTS                                              *
 *  SECTION 1 - PER-BROWSER CHOICES                                *
 *  SECTION 2 - THE FILTER RAIL                                    *
 *  SECTION 3 - VIEW TYPE AND RECORDS PER PAGE                     *
 *  SECTION 4 - ONE ZOHO CARD ROW                                  *
 *****************************************************************/

import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Icon } from './customerIcons';
import { Sheet, StatusChip } from './customerParts';
import { formatMoment, formatDay } from './customerDates';
import { readCellWords } from './RecordList';

/*****************************************************************
 *  SECTION 1 - PER-BROWSER CHOICES                                *
 *****************************************************************/

const VIEW_TYPE_KEY = 'cb-list-view-type';     // 'table' or 'cards'
const RAIL_FOLDED_KEY = 'cb-list-rail-folded';  // 'yes' when folded away
export const VIEW_TYPE_TABLE = 'table';
export const VIEW_TYPE_CARDS = 'cards';

/* ---- readChoice(storageKey, fallback) -- a remembered word, or the fallback ---- */
function readChoice(storageKey, fallback) {
  try {
    return window.localStorage.getItem(storageKey) || fallback;
  } catch (storageProblem) {
    return fallback;
  }
}

/* ---- keepChoice(storageKey, value) -- remembers if it can ---- */
function keepChoice(storageKey, value) {
  try {
    window.localStorage.setItem(storageKey, value);
  } catch (storageProblem) {
    // storage blocked: the choice lasts until the page reloads
  }
}

/* ---- useViewType() -- Out: [viewType, chooseViewType] (table by default) ---- */
export function useViewType() {
  const [viewType, setViewType] = useState(() => (readChoice(VIEW_TYPE_KEY, VIEW_TYPE_TABLE) === VIEW_TYPE_CARDS ? VIEW_TYPE_CARDS : VIEW_TYPE_TABLE));

  function chooseViewType(nextType) {
    keepChoice(VIEW_TYPE_KEY, nextType);
    setViewType(nextType);
  }

  return [viewType, chooseViewType];
}

/* ---- useRailFolded() -- Out: [isFolded, flipFolded] ---- */
export function useRailFolded() {
  const [isFolded, setIsFolded] = useState(() => readChoice(RAIL_FOLDED_KEY, 'no') === 'yes');

  function flipFolded() {
    keepChoice(RAIL_FOLDED_KEY, isFolded ? 'no' : 'yes');
    setIsFolded(!isFolded);
  }

  return [isFolded, flipFolded];
}

/*****************************************************************
 *  SECTION 2 - THE FILTER RAIL                                    *
 *****************************************************************/

/* ---- FieldFilterList({ fields, drafts, onDraft }) ----------------
   Every field as a checkbox (Zoho's "Filter By Fields"); a ticked
   field shows its "contains" box.                               */
function FieldFilterList({ fields, drafts, onDraft }) {
  const [findWords, setFindWords] = useState('');
  const shownFields = fields.filter((oneField) => oneField.label.toLowerCase().includes(findWords.trim().toLowerCase()));

  return (
    <div className="cb-zl-fields">
      <label className="cb-zl-find">
        <span className="cb-hidden-label">Find a field</span>
        <Icon name="search" />
        <input className="cb-hr-search" type="search" value={findWords} placeholder="Search" onChange={(changeEvent) => setFindWords(changeEvent.target.value)} />
      </label>
      <ul className="cb-zl-field-list">
        {shownFields.map((oneField) => {
          const draft = drafts[oneField.key];
          const isTicked = draft !== undefined;
          const boxId = `cb-zl-f-${oneField.key}`;

          return (
            <li key={oneField.key}>
              <label className="cb-zl-tick" htmlFor={boxId}>
                <input
                  id={boxId}
                  type="checkbox"
                  checked={isTicked}
                  onChange={(changeEvent) => onDraft(oneField, changeEvent.target.checked ? '' : undefined)}
                />
                {oneField.label}
              </label>
              {isTicked ? (
                <input
                  className="cb-input cb-zl-contains"
                  aria-label={`${oneField.label} contains`}
                  placeholder="contains"
                  value={draft}
                  onChange={(changeEvent) => onDraft(oneField, changeEvent.target.value)}
                />
              ) : null}
            </li>
          );
        })}
      </ul>
    </div>
  );
}

/* ---- RailBody(railParts) -- the rail's inside (shared by desktop and the phone sheet) ----
   In: { title, views, chosenView, onChooseView, fields (or null),
         appliedFilters, onApply }.                               */
function RailBody(railParts) {
  const { title, views, chosenView, onChooseView, fields, appliedFilters, onApply } = railParts;
  const startDrafts = {};

  for (const oneFilter of appliedFilters) {
    startDrafts[oneFilter.key] = oneFilter.contains;
  }

  const [drafts, setDrafts] = useState(startDrafts);  // key -> words being typed
  const [labels, setLabels] = useState(() => {
    const startLabels = {};

    for (const oneFilter of appliedFilters) {
      startLabels[oneFilter.key] = oneFilter.label;
    }

    return startLabels;
  });

  /* ---- draftOne(field, words) -- undefined = untick ---- */
  function draftOne(field, words) {
    setDrafts((draftsNow) => {
      const nextDrafts = { ...draftsNow };

      if (words === undefined) {
        delete nextDrafts[field.key];
      } else {
        nextDrafts[field.key] = words;
      }

      return nextDrafts;
    });
    setLabels((labelsNow) => ({ ...labelsNow, [field.key]: field.label }));
  }

  /* ---- applyDrafts() / clearAll() ---- */
  function applyDrafts() {
    const filterList = Object.keys(drafts)
      .filter((oneKey) => String(drafts[oneKey]).trim())
      .map((oneKey) => ({ key: oneKey, label: labels[oneKey] || oneKey, contains: String(drafts[oneKey]).trim() }));

    onApply(filterList);
  }

  function clearAll() {
    setDrafts({});
    onApply([]);
  }

  return (
    <>
      <h2 className="cb-zr-rail-head">{title}</h2>
      <h3 className="cb-zl-rail-sub">Saved views</h3>
      <ul className="cb-zr-rail-list">
        {views.map((oneView) => (
          <li key={oneView.key}>
            <button
              type="button"
              className={oneView.key === chosenView ? 'cb-zr-rail-link is-chosen' : 'cb-zr-rail-link'}
              aria-pressed={oneView.key === chosenView ? 'true' : 'false'}
              onClick={() => onChooseView(oneView.key)}
            >
              {oneView.label}
            </button>
          </li>
        ))}
      </ul>
      {fields ? (
        <>
          <h3 className="cb-zl-rail-sub">Filter By Fields</h3>
          <FieldFilterList fields={fields} drafts={drafts} onDraft={draftOne} />
          <div className="cb-zl-rail-acts">
            <button type="button" className="bc-btn is-primary bc-btn-auto cb-hr-btn-sm" onClick={applyDrafts}>Apply filter</button>
            {appliedFilters.length > 0 ? (
              <button type="button" className="bc-btn is-ghost bc-btn-auto cb-hr-btn-sm" onClick={clearAll}>Clear</button>
            ) : null}
          </div>
        </>
      ) : null}
    </>
  );
}

/* ---- FilterRail(railParts) ---------------------------------------
   Desktop: a column that folds away ("Hide filters"). The phone
   uses PhoneFilterButton instead (a sheet).                     */
export function FilterRail(railParts) {
  const [isFolded, flipFolded] = useRailFolded();

  if (isFolded) {
    return (
      <aside className="cb-zl-rail is-folded" aria-label={railParts.title}>
        <button type="button" className="cb-zl-fold" onClick={flipFolded} title="Show filters" aria-label="Show filters">
          <Icon name="arrowright" />
        </button>
      </aside>
    );
  }

  return (
    <aside className="cb-zl-rail" aria-label={railParts.title}>
      <button type="button" className="cb-zl-fold" onClick={flipFolded} title="Hide filters" aria-label="Hide filters">
        <Icon name="arrowleft" />
      </button>
      <RailBody {...railParts} />
    </aside>
  );
}

/* ---- PhoneFilterButton(railParts) -- "Filter" opens the rail as a sheet ---- */
export function PhoneFilterButton(railParts) {
  const [isOpen, setIsOpen] = useState(false);
  const activeCount = railParts.appliedFilters.length;

  return (
    <>
      <button type="button" className="cb-hr-tab cb-zl-phone-filter" onClick={() => setIsOpen(true)}>
        <Icon name="search" /> Filter{activeCount > 0 ? ` (${activeCount})` : ''}
      </button>
      {isOpen ? (
        <Sheet title={railParts.title} onClose={() => setIsOpen(false)}>
          <RailBody
            {...railParts}
            onChooseView={(viewKey) => {
              railParts.onChooseView(viewKey);
              setIsOpen(false);
            }}
            onApply={(filterList) => {
              railParts.onApply(filterList);
              setIsOpen(false);
            }}
          />
        </Sheet>
      ) : null}
    </>
  );
}

/* ---- AppliedFilterChips({ filters, onClear }) -- what is filtering the list now ---- */
export function AppliedFilterChips({ filters, onClear }) {
  if (filters.length === 0) {
    return null;
  }

  return (
    <div className="cb-zl-applied">
      {filters.map((oneFilter) => (
        <StatusChip key={oneFilter.key} tone="brand" icon="search" word={`${oneFilter.label} contains "${oneFilter.contains}"`} />
      ))}
      <button type="button" className="cb-link-btn" onClick={onClear}>Clear filters</button>
    </div>
  );
}

/*****************************************************************
 *  SECTION 3 - VIEW TYPE AND RECORDS PER PAGE                     *
 *****************************************************************/

/* ---- ViewTypeSwitch({ viewType, onChoose }) -- Table | Cards ---- */
export function ViewTypeSwitch({ viewType, onChoose }) {
  const choices = [
    { key: VIEW_TYPE_TABLE, words: 'Table', icon: 'file' },
    { key: VIEW_TYPE_CARDS, words: 'Cards', icon: 'people' },
  ];

  return (
    <div className="cb-zl-viewtype" role="group" aria-label="View type">
      {choices.map((oneChoice) => (
        <button
          key={oneChoice.key}
          type="button"
          className="cb-hr-tab"
          aria-pressed={viewType === oneChoice.key ? 'true' : 'false'}
          onClick={() => onChoose(oneChoice.key)}
        >
          <Icon name={oneChoice.icon} />
          {oneChoice.words}
        </button>
      ))}
    </div>
  );
}

const PER_PAGE_CHOICES = [10, 25, 50, 100]; // the door's ceiling is 100

/* ---- PerPagePicker({ perPage, onChoose }) -- "Records Per Page" ---- */
export function PerPagePicker({ perPage, onChoose }) {
  return (
    <label className="cb-zl-perpage">
      <span>Records per page</span>
      <select className="cb-hr-look" value={perPage} onChange={(changeEvent) => onChoose(Number(changeEvent.target.value))}>
        {PER_PAGE_CHOICES.map((oneChoice) => <option key={oneChoice} value={oneChoice}>{oneChoice}</option>)}
      </select>
    </label>
  );
}

/*****************************************************************
 *  SECTION 4 - ONE ZOHO CARD ROW                                  *
 *****************************************************************/

/* ---- ownerWordsOf(row) -- Zoho's record owner, kept as Zoho sent it ---- */
export function ownerWordsOf(row) {
  const customValues = row.customFields || {};
  const ownerValue = customValues.Owner;

  if (ownerValue && typeof ownerValue === 'object') {
    return ownerValue.name || '';
  }

  return ownerValue ? String(ownerValue) : '';
}

/* ---- ZohoCardRow({ row, nameFor, cardLines, linkFor }) -----------
   cardLines: [{ label, column }] - the lines under the name.     */
export function ZohoCardRow({ row, nameFor, cardLines, linkFor }) {
  const nameWords = nameFor(row);
  const firstLetter = String(nameWords || '?').trim().charAt(0).toUpperCase() || '?';
  const ownerWords = ownerWordsOf(row);
  const isWaitingToSync = row._syncStatus === 'pending';
  const rowLink = isWaitingToSync ? null : linkFor(row);
  const nextTask = row.nextTask || null;

  const inside = (
    <>
      <span className="cb-zl-letter" aria-hidden="true">{firstLetter}</span>
      <span className="cb-zl-card-main">
        <span className="cb-zl-card-name">{nameWords}</span>
        <span className="cb-zl-card-lines">
          {cardLines.map((oneLine) => (
            <span key={oneLine.label} className="cb-zl-card-line">
              <span className="cb-zl-card-label">{oneLine.label}</span>
              {readCellWords(row, oneLine.column) || '—'}
            </span>
          ))}
        </span>
      </span>
      <span className="cb-zl-card-side">
        <span className="cb-zl-card-owner">{ownerWords || 'No owner'}</span>
        <span className="cb-zl-card-when">{formatMoment(row.updatedAt)}</span>
        {nextTask ? <StatusChip tone="warn" icon="check" word={`Task ${formatDay(nextTask.dueAt)}`} /> : null}
        {isWaitingToSync ? <StatusChip tone="warn" icon="offline" word="Not synced yet" /> : null}
      </span>
    </>
  );

  if (!rowLink) {
    return <li><div className="cb-zl-card">{inside}</div></li>;
  }

  return <li><Link className="cb-zl-card" to={rowLink}>{inside}</Link></li>;
}
